// src/features/payment/WebhookEventsTab.tsx
// Provider webhook events (Stripe/Adyen callbacks) — inspect and retry failed
// processing. HandleProviderWebhook itself is a system RPC: providers call it
// directly with a signed payload, never a person.
// Covers: ListProviderEvents, RetryProviderEvent.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { usePaymentWebhook, useI18n } from '@react/hooks';
import {
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { formatDateTime } from './format';

export const WebhookEventsTab = observer(() => {
  const store = usePaymentWebhook();
  const { t } = useI18n();
  const [retryingId, setRetryingId] = useState<string | null>(null);

  useEffect(() => {
    store.ListProviderEvents({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const handleRetry = async (id: string) => {
    setRetryingId(id);
    try {
      await store.RetryProviderEvent(id, {} as any);
      await store.ListProviderEvents({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('payment.events.subtitle')}</p>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('payment.events.type')}</TableHead>
            <TableHead>{t('payment.events.providerId')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead>{t('payment.events.retryCount')}</TableHead>
            <TableHead>{t('payment.common.created')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={6} message={t('payment.events.empty')} />
          ) : (
            store.items.map((ev) => (
              <TableRow key={ev.entity?.id}>
                <TableCell>{ev.event_type || ev.provider_event_type}</TableCell>
                <TableCell className="font-mono text-xs">{ev.provider_event_id}</TableCell>
                <TableCell>
                  {ev.processed ? (
                    <Badge variant="success" size="sm">{t('payment.events.processed')}</Badge>
                  ) : ev.processing_error ? (
                    <Badge variant="error" size="sm">{t('payment.events.failed')}</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">{t('payment.events.pending')}</Badge>
                  )}
                </TableCell>
                <TableCell>{ev.retry_count}</TableCell>
                <TableCell>{formatDateTime(ev.created_at)}</TableCell>
                <TableCell className="text-right">
                  {!ev.processed && (
                    <Button variant="ghost" size="sm" isLoading={retryingId === ev.entity?.id} onClick={() => ev.entity?.id && handleRetry(ev.entity.id)}>
                      {t('payment.common.retry')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
});

export default WebhookEventsTab;
